import moment from "moment"
import "moment/locale/fr"
import {
    FUNNEL_BESOINS,
    toggleBesoinInList,
} from "@data/constants/funnelBesoinsConstants"

/**
 * Contenu statique du funnel Stage — UNIQUEMENT du contenu de questionnaire
 * (objectifs, niveaux, besoins spécifiques) et des helpers de normalisation du
 * catalogue API. ZÉRO constante de prix, de session, d'horaire ou de date :
 * tout cela vient du catalogue (GET /stage-funnel/{stateId} → config), c'est le
 * principe même du funnel (lancer un stage = le créer dans le BO, zéro déploi).
 */

// Besoins spécifiques élève — chips reprises du diagnostic brevet
// (BrevetDiagnosticStep2Screen). « Aucun » est exclusif. PAS de champ texte
// libre dans ce MVP (décision produit).
// Les chips « spécificités » sont partagées avec le funnel d'inscription :
// définition unique dans @data/constants/funnelBesoinsConstants, ré-exportée
// ici pour ne pas casser les imports existants du parcours stage.
export { toggleBesoinInList }
// La question du stage parle de « spécificités » : la chip qui les nie
// s'accorde au féminin. Le reste de la liste est celui des autres funnels.
export const STAGE_BESOINS = FUNNEL_BESOINS.map((besoin) =>
    besoin.id === "aucun" ? { ...besoin, label: "Aucune" } : besoin
)

// ---------------------------------------------------------------------------
// Normalisation du catalogue API (config servie par GET /{stateId}).
// Le backend est développé en parallèle : on lit les clés attendues du contrat
// en tolérant les variantes de nommage plausibles, et on ne recalcule JAMAIS un
// prix/une date côté front.
// ---------------------------------------------------------------------------

// Jour (code anglais ou libellé) → libellé français.
const DAY_LABELS = {
    mon: "Lundi",
    tue: "Mardi",
    wed: "Mercredi",
    thu: "Jeudi",
    fri: "Vendredi",
    sat: "Samedi",
    sun: "Dimanche",
    monday: "Lundi",
    tuesday: "Mardi",
    wednesday: "Mercredi",
    thursday: "Jeudi",
    friday: "Vendredi",
    saturday: "Samedi",
    sunday: "Dimanche",
}

export const formatDayLabel = (day) => {
    if (!day) return ""
    const known = DAY_LABELS[String(day).toLowerCase()]
    if (known) return known
    const raw = String(day)
    return raw.charAt(0).toUpperCase() + raw.slice(1)
}

// "10:30" → "10h30" (déjà "10h30" → inchangé).
export const formatTimeLabel = (time) =>
    typeof time === "string" ? time.replace(":", "h") : ""

// Durée en minutes → "1 h" / "1 h 30". Tolère une durée déjà formatée (string).
export const formatDurationLabel = (duration) => {
    if (duration == null) return ""
    if (typeof duration === "string" && !/^\d+$/.test(duration)) return duration
    const minutes = Number(duration)
    if (!Number.isFinite(minutes) || minutes <= 0) return ""
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    if (h === 0) return `${m} min`
    return m ? `${h} h ${m < 10 ? "0" : ""}${m}` : `${h} h`
}

// Une séance d'horaire ({day|jour, time|heure|start_time, duration|duree|duration_minutes})
// → "Lundi 10h30 (1 h)".
export const formatSlotSession = (session) => {
    if (!session) return ""
    if (typeof session === "string") return session
    const day = formatDayLabel(session.day ?? session.jour)
    const time = formatTimeLabel(
        session.time ?? session.heure ?? session.start_time
    )
    const duration = formatDurationLabel(
        session.duration_minutes ?? session.duration ?? session.duree
    )
    return (
        [day, time].filter(Boolean).join(" ") +
        (duration ? ` (${duration})` : "")
    )
}

// --- Template ---
export const getTemplateFromConfig = (config) =>
    config?.template ?? config?.course_template ?? null

export const getTemplateName = (config) => {
    const template = getTemplateFromConfig(config)
    return template?.name ?? template?.label ?? config?.template_name ?? null
}

// Scope de classes du template (M2M vide = toutes → null).
export const getTemplateClasses = (config) => {
    const template = getTemplateFromConfig(config)
    const classes =
        template?.student_classes ??
        template?.classes ??
        config?.student_classes ??
        null
    return Array.isArray(classes) && classes.length ? classes : null
}

// Liste brute de dates → liste ISO ("YYYY-MM-DD"). Tolère ["YYYY-MM-DD", …]
// ou [{ date|start_date, … }, …].
const normalizeStartDates = (raw) => {
    if (!Array.isArray(raw)) return []
    return raw
        .map((d) => (typeof d === "string" ? d : (d?.date ?? d?.start_date)))
        .filter(Boolean)
}

// --- Pré-inscription (montant réellement encaissé aujourd'hui) ---
// Le funnel encaisse TOUJOURS le SKU fixe 1 € (`pre_inscription_1eur_ht`),
// identique brevet/été — le solde du stage est prélevé au démarrage. C'est CE
// montant qui s'affiche « aujourd'hui / acompte », et le solde = total − 1 € ;
// JAMAIS le `deposit_cents` du catalogue (réservé à d'autres modes de vente BO,
// où il peut valoir ≠ 100). Servi par le backend (`preinscription_cents`), avec
// repli sur la constante produit quand la config n'est pas (encore) chargée.
export const STAGE_PREINSCRIPTION_CENTS = 100
export const getPreinscriptionCents = (config) =>
    config?.preinscription_cents ??
    config?.preinscriptionCents ??
    STAGE_PREINSCRIPTION_CENTS

// --- Variantes (compat) ---
// Le FE ne compose PLUS sur les variantes (pivot « composition par sessions » :
// le prix vient de pricing_by_count). Elles restent servies et lues UNIQUEMENT
// pour retrouver le rythme (sessions_per_week) de la variante d'un count —
// affichage « soit X €/heure » — via pricing.variantKey.
// Forme normalisée : { key, label, weeksCount, sessionsPerWeek, priceCents,
// depositCents, startDates }.
export const getVariantsFromConfig = (config) => {
    const raw = config?.variants ?? config?.variantes ?? []
    if (!Array.isArray(raw)) return []
    return raw
        .filter((v) => v && v.on_sale !== false && v.is_on_sale !== false)
        .map((v) => ({
            key: v.key ?? v.variant_key ?? v.slug ?? String(v.id ?? ""),
            label: v.label ?? v.name ?? null,
            weeksCount: v.weeks_count ?? v.weeks ?? null,
            sessionsPerWeek: v.sessions_per_week ?? v.sessionsPerWeek ?? null,
            priceCents: v.price_cents ?? v.priceCents ?? null,
            depositCents: v.deposit_cents ?? v.depositCents ?? null,
            startDates: normalizeStartDates(
                v.start_dates ?? v.startDates ?? []
            ),
        }))
        .filter((v) => v.key)
}

// --- Horaires (créneaux hebdo du template) ---
// Forme normalisée : { key, label, sessions: [raw…], sessionLabels: [string…] }.
export const getSlotsFromConfig = (config) => {
    const raw = config?.slots ?? config?.schedules ?? config?.horaires ?? []
    if (!Array.isArray(raw)) return []
    return raw
        .map((s) => {
            const sessions = s?.sessions ?? s?.seances ?? []
            const sessionLabels = (Array.isArray(sessions) ? sessions : [])
                .map(formatSlotSession)
                .filter(Boolean)
            return {
                key: s?.slot_key ?? s?.key ?? s?.slug ?? String(s?.id ?? ""),
                label: s?.label ?? s?.name ?? null,
                sessions: Array.isArray(sessions) ? sessions : [],
                sessionLabels,
            }
        })
        .filter((s) => s.key)
}

// Nombre de séances d'une semaine de stage, lu sur l'horaire du catalogue —
// null tant qu'il n'est pas connu (on ne l'invente pas).
export const getSeancesPerWeek = (config) =>
    getSlotsFromConfig(config)[0]?.sessions.length || null

// ---------------------------------------------------------------------------
// Composition par sessions (contrat v2) — une session = 1 date de la saison →
// la semaine de séances de l'horaire. Multi-sélection, rien de pré-coché,
// non contiguës OK. Le prix vient de pricing_by_count (jamais recalculé), le
// cap de matières par session de max_subjects_per_session (source backend).
// ---------------------------------------------------------------------------

// "2026-08-03" → "lun 3 août" (sans le point d'abréviation moment) —
// `withMonth: false` pour élider le mois côté début quand il est partagé.
const formatShortSessionDay = (iso, { withMonth = true } = {}) =>
    moment(iso)
        .locale("fr")
        .format(withMonth ? "ddd D MMM" : "ddd D")
        .replace(/\./g, "")

// Libellé d'une semaine de stage quand le catalogue n'en fournit pas :
// "Semaine 1 — du lun 3 au ven 7 août" (mois élidé au début quand les deux
// bornes le partagent).
export const sessionFallbackLabel = ({ startDate, endDate }, index) => {
    const ordinal = `Semaine ${index + 1}`
    if (startDate && endDate) {
        const sameMonth =
            moment(startDate).format("YYYY-MM") ===
            moment(endDate).format("YYYY-MM")
        const start = formatShortSessionDay(startDate, {
            withMonth: !sameMonth,
        })
        return `${ordinal} — du ${start} au ${formatShortSessionDay(endDate)}`
    }
    if (startDate) {
        // Pas de « semaine du » ici : « Semaine 2 — semaine du … » serait
        // redondant → « Semaine 2 — du lun 10 août ».
        return `${ordinal} — du ${formatShortSessionDay(startDate)}`
    }
    return ordinal
}

// --- Sessions proposées (dérivées saison × horaire côté backend) ---
// Forme normalisée : { key, startDate, endDate, full, label }. La DATE ISO de
// début est l'identité de sélection ET la valeur wire (PUT answers / checkout).
// `full` (bool) = la semaine est entrée dans son délai plancher (48h avant la
// 1ʳᵉ séance, dimanche non compté — MÊME règle que le funnel été) : elle passe
// « Complet », non cochable. Le backend fait foi (re-validé au checkout).
export const getSessionsFromConfig = (config) => {
    const raw = config?.sessions ?? []
    if (!Array.isArray(raw)) return []
    return raw
        .map((s, index) => {
            const startDate =
                typeof s === "string"
                    ? s
                    : (s?.start_date ?? s?.startDate ?? null)
            if (!startDate) return null
            const endDate =
                typeof s === "string"
                    ? null
                    : (s?.end_date ?? s?.endDate ?? null)
            return {
                key: s?.key ?? startDate,
                startDate,
                endDate,
                full: typeof s === "string" ? false : s?.full === true,
                label:
                    s?.label ??
                    sessionFallbackLabel({ startDate, endDate }, index),
                // Unité créneau : le début de session auquel l'item appartient
                // (le week-end) et son intertitre. Absents en unité semaine —
                // un item de semaine EST sa propre session.
                sessionStart: s?.session_start ?? startDate,
                sessionLabel: s?.session_label ?? null,
                // L'instant EXACT où ce créneau cesse d'être vendable, calculé
                // par le backend sur la même règle que « Complet ». Le compte
                // à rebours s'y adosse : le front ne redérive jamais le délai,
                // il l'afficherait forcément à côté.
                closesAt: typeof s === "string" ? null : (s?.closes_at ?? null),
            }
        })
        .filter(Boolean)
}

// --- Unité de vente de la formule ---
// `week` : le parent coche des SEMAINES, l'identité de sélection est une date.
// `seance` : il coche des CRÉNEAUX de 2 h, l'identité est une date + heure.
// Servie par le catalogue — jamais devinée de la forme des clés, qui accepte
// les deux en permanence (un parcours engagé avant un déploiement finit sur
// l'ancienne forme).
export const SELLING_UNIT_WEEK = "week"
export const SELLING_UNIT_SEANCE = "seance"

export const getSellingUnit = (config) =>
    getTemplateFromConfig(config)?.selling_unit ?? SELLING_UNIT_WEEK

export const sellsBySeance = (config) =>
    getSellingUnit(config) === SELLING_UNIT_SEANCE

/**
 * Bornes de retouche du programme pour la classe de l'élève, SERVIES par le
 * backend. Ne pas réimplémenter les bandes de classe ici : deux écritures
 * dériveraient, et l'écran finirait par proposer ce que l'API refuse.
 * Repli prudent (1 seul chapitre) quand le catalogue n'a rien dit.
 */
export const getChapterBounds = (config) => {
    const bounds = getTemplateFromConfig(config)?.chapter_bounds
    const min = Number(bounds?.min)
    const max = Number(bounds?.max)
    return {
        min: Number.isFinite(min) && min > 0 ? min : 1,
        max: Number.isFinite(max) && max > 0 ? max : 1,
    }
}

/**
 * Les items groupés par session, dans l'ordre du catalogue — ce que l'écran
 * rend sous un intertitre par week-end. En unité semaine chaque session ne
 * contient qu'elle-même, donc le même rendu marche pour les deux.
 *
 * @returns {Array<{ start: string, label: string|null, items: Array }>}
 */
export const groupSessionsByStart = (sessions) => {
    const order = []
    const byStart = {}
    ;(sessions ?? []).forEach((session) => {
        const start = session.sessionStart ?? session.startDate
        if (!byStart[start]) {
            byStart[start] = { start, label: session.sessionLabel, items: [] }
            order.push(start)
        }
        byStart[start].items.push(session)
    })
    return order.map((start) => byStart[start])
}

// --- Remise affichée ---
// TOUJOURS calculée des deux montants, jamais servie par l'API : un
// pourcentage stocké finirait par contredire les prix qu'il accompagne.
// Null quand il n'y a pas de promo à lire.
export const discountPercent = (priceCents, compareAtCents) => {
    if (!compareAtCents || !priceCents || compareAtCents <= priceCents)
        return null
    return Math.round(((compareAtCents - priceCents) / compareAtCents) * 100)
}

// Ancre ABSOLUE de la saison (date ISO de début la plus ancienne, y compris
// déjà passée / masquée) — servie par le backend. C'est la référence FIXE de
// l'index de rareté, identique à la LP : « Semaine 1 » ne glisse pas quand les
// 1ʳᵉˢ semaines démarrent et sortent de la liste des sessions offertes.
export const getSeasonFirstStart = (config) =>
    config?.season_first_start ?? null

// --- Prix par nombre de sessions cochées ---
// pricing_by_count = { "1": { variant_key, price_cents,
// remainder_after_preinscription_cents }, … } — UNIQUEMENT les counts couverts
// par une variante en vente : un count absent n'est PAS proposable (l'admin
// ajoute la variante en BO pour le vendre).
export const getPricingByCount = (config) => {
    const raw = config?.pricing_by_count ?? config?.pricingByCount ?? null
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}
    const normalized = {}
    Object.entries(raw).forEach(([count, entry]) => {
        const n = Number(count)
        const priceCents = entry?.price_cents ?? entry?.priceCents ?? null
        if (!Number.isInteger(n) || n <= 0 || priceCents == null) return
        normalized[n] = {
            variantKey: entry.variant_key ?? entry.variantKey ?? null,
            priceCents,
            // Prix de référence (le barré). Null = pas de promo sur ce palier.
            compareAtCents:
                entry.compare_at_price_cents ?? entry.compareAtCents ?? null,
            remainderAfterPreinscriptionCents:
                entry.remainder_after_preinscription_cents ??
                entry.remainderAfterPreinscriptionCents ??
                null,
        }
    })
    return normalized
}

export const pricingForCount = (pricingByCount, count) =>
    (pricingByCount && pricingByCount[count]) || null

// « dès X € » avant le premier coche : le prix plancher des counts en vente.
export const getMinPricingCents = (pricingByCount) => {
    const prices = Object.values(pricingByCount || {})
        .map((p) => p?.priceCents)
        .filter((c) => c != null)
    return prices.length ? Math.min(...prices) : null
}

// Plus grand nombre de sessions atteignable en cochant une à une : les counts
// se traversent séquentiellement (une case à la fois), donc un trou dans
// pricing_by_count bloque tout ce qui est au-delà — les cases restantes sont
// grisées avec un « N sessions et + : nous contacter ».
export const getMaxSelectableSessions = (pricingByCount) => {
    let n = 0
    while (pricingForCount(pricingByCount, n + 1)) n += 1
    return n
}

// --- Durée d'une séance (minutes) ---
// Source : le template du catalogue (`session_duration_minutes`), repli sur la
// durée de la première séance du premier horaire structuré. Null quand aucune
// source n'est disponible — l'affichage €/heure se masque alors plutôt que
// d'inventer une durée.
export const getSessionDurationMinutes = (config) => {
    const template = getTemplateFromConfig(config)
    const fromTemplate =
        template?.session_duration_minutes ?? config?.session_duration_minutes
    if (Number.isFinite(Number(fromTemplate)) && Number(fromTemplate) > 0) {
        return Number(fromTemplate)
    }
    const fromSlot = getSlotsFromConfig(config)[0]?.sessions?.[0]
    const duration = fromSlot?.duration_minutes ?? fromSlot?.duration
    return Number.isFinite(Number(duration)) && Number(duration) > 0
        ? Number(duration)
        : null
}

// --- Prix à l'HEURE (mention « soit X €/heure ») ---
// Simple division d'affichage du total catalogue par le volume horaire réel :
// nb sessions cochées × séances/semaine × durée de séance (les séances font
// 2 h — un prix « par séance » serait trompeur). JAMAIS un prix recalculé.
// Null dès qu'une composante manque : la mention se masque.
export const hourlyPriceCents = ({
    priceCents,
    sessionsCount,
    seancesPerWeek,
    sessionDurationMinutes,
}) => {
    if (
        !priceCents ||
        !sessionsCount ||
        !seancesPerWeek ||
        !sessionDurationMinutes
    ) {
        return null
    }
    const totalHours =
        (sessionsCount * seancesPerWeek * sessionDurationMinutes) / 60
    if (!(totalHours > 0)) return null
    return Math.round(priceCents / totalHours)
}

// --- Cap de matières PAR session cochée ---
// Calculé par le backend depuis la classe du state (CE2→3ème = 2 — la semaine
// se partage 1h + 1h chaque jour —, lycée 1). Mirroré ici, re-validé backend.
export const getMaxSubjectsPerSession = (config) => {
    const cap =
        config?.max_subjects_per_session ?? config?.maxSubjectsPerSession
    return Number.isInteger(cap) && cap > 0 ? cap : 1
}

// --- FAQ ---
export const getFaqsFromConfig = (config) => {
    const raw = config?.faq ?? config?.faqs ?? []
    return Array.isArray(raw) ? raw : []
}

// ---------------------------------------------------------------------------
// Rareté « X places restantes » — FORMULE DÉTERMINISTE, miroir EXACT de la LP
// Lovable (Julien) : le même chiffre s'affiche des deux côtés, ZÉRO random.
// Elle ne dépend que de (a) la date de début de la session, (b) la 1ʳᵉ session
// de la saison (référence de l'index stable) et (c) le jour courant — ce qui la
// rend organique (le chiffre bouge un peu d'un jour à l'autre) tout en restant
// reproductible pour un jour donné. Aucun état, aucune persistance : une pure
// fonction, testée sur des valeurs attendues.
// ---------------------------------------------------------------------------

// Les stages dont la LP affiche ce compteur. La formule est le miroir d'une
// LP : sur un stage dont la LP ne le montre pas, le funnel annoncerait un
// chiffre que la famille n'a vu nulle part. Un nouveau stage n'y entre que si
// sa LP le reprend.
const SCARCITY_TEMPLATE_SLUGS = ["pre_rentree", "boost_weekend"]

export const showsScarcity = (config) =>
    SCARCITY_TEMPLATE_SLUGS.includes(getTemplateFromConfig(config)?.slug)

export const SCARCITY_MIN_PLACES = 5
export const SCARCITY_MAX_PLACES = 25

// Popularité additionnelle (retranchée aux places) par index de session : la
// dernière session part plus vite (rush pré-rentrée). 0 au-delà des index
// couverts — un index sans entrée n'ajoute aucune pression.
const SESSION_POPULARITY = { 1: 0, 2: 0, 3: 0, 4: 8 }

const SCARCITY_MS_PER_DAY = 24 * 60 * 60 * 1000

// "2026-08-03" (ou une Date) → minuit LOCAL. On raisonne en jours pleins et non
// en instants, pour un résultat stable quel que soit le fuseau / l'heure.
const scarcityMidnight = (value) => {
    if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate())
    }
    if (typeof value !== "string") return null
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return null
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

// Nombre de jours pleins entre deux minuits (arrondi : absorbe le DST).
const scarcityWholeDays = (from, to) =>
    Math.round((to - from) / SCARCITY_MS_PER_DAY)

// Jour de l'année (1..366) d'une date locale.
const scarcityDayOfYear = (date) =>
    scarcityWholeDays(new Date(date.getFullYear(), 0, 1), date) + 1

const scarcityClamp = (value, min, max) => Math.max(min, Math.min(max, value))

/**
 * Rareté d'une session pour le signal FOMO du configurateur — identique à la LP.
 *
 * @param {Object} params
 * @param {string} params.startDate        date ISO de début de la session cochable
 * @param {string} [params.seasonFirstStart] date ISO de la 1ʳᵉ session de la
 *   saison (référence de l'index stable). Défaut : la session elle-même (index 1).
 * @param {Date}   [params.now]            « aujourd'hui » (injectable pour les tests)
 * @returns {{ index:number, places:number, gaugeFill:number } | null}
 *   `places` ∈ [5,25], `gaugeFill` ∈ [0,1] = (25 − places) / 25 (plus la session
 *   est prisée/proche, plus la jauge est pleine). `null` si la date est invalide.
 */
export const stageSessionScarcity = ({
    startDate,
    seasonFirstStart,
    now = new Date(),
} = {}) => {
    const start = scarcityMidnight(startDate)
    const first = scarcityMidnight(seasonFirstStart ?? startDate)
    const today = scarcityMidnight(now)
    if (!start || !first || !today) return null

    // Index stable 1-based : la 1ʳᵉ session de la saison = 1, +1 par semaine —
    // 3/8 → 1, 10/8 → 2, 17/8 → 3, 24/8 → 4 (comme la LP).
    const index = Math.round(scarcityWholeDays(first, start) / 7) + 1
    // Jours pleins d'ici le début de la session.
    const joursAvant = scarcityWholeDays(today, start)
    const jour = scarcityDayOfYear(today)
    // Bruit entier −5..+5, déterministe pour un (index, jour) donné.
    const bruit = ((index * 7 + jour * 5) % 11) - 5
    const popularity = SESSION_POPULARITY[index] ?? 0
    const places = scarcityClamp(
        Math.round(0.6 * joursAvant) - popularity + bruit,
        SCARCITY_MIN_PLACES,
        SCARCITY_MAX_PLACES
    )
    return {
        index,
        places,
        gaugeFill: (SCARCITY_MAX_PLACES - places) / SCARCITY_MAX_PLACES,
    }
}

// --- Répartition des créneaux entre les matières ---
// Le parent ne saisit JAMAIS un nombre d'heures : on répartit à parts égales et
// le reste va aux premières matières choisies, dans son ordre de sélection.
// C'est ce qui remplace le budget d'heures à faire tomber juste — l'écart ne
// lui pose plus aucune question.
//
// @returns {Object<string, number>} id matière → nombre de créneaux
export const distributeSlots = (subjectIds, slotCount) => {
    const ids = (subjectIds ?? []).map(String)
    if (!ids.length || !slotCount) return {}
    const base = Math.floor(slotCount / ids.length)
    let remainder = slotCount % ids.length
    const out = {}
    ids.forEach((id) => {
        out[id] = base + (remainder > 0 ? 1 : 0)
        if (remainder > 0) remainder -= 1
    })
    return out
}

// Les volumes horaires que le backend attend (`session_subject_hours`), dérivés
// de la répartition ci-dessus. La donnée ne disparaît pas du contrat — elle
// cesse simplement d'être demandée au parent.
export const hoursFromDistribution = (distribution, sessionHours) =>
    Object.fromEntries(
        Object.entries(distribution ?? {}).map(([id, slots]) => [
            id,
            slots * sessionHours,
        ])
    )
